import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios/index";
import { serialize } from "object-to-formdata";
import { axiosApi } from "./axios";
import { useSnackbar } from "../components/snackbar";
import { IPatchParams, IPostParams } from "../types/api";

const useApiServices = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Post service
  const usePostApi = <T,>({
    url,
    urlAfterSuccess,
    onSuccess,
    onError,
    withSuccessNotistack = true,
    withErrorNotistack = true,
    withFormData = false,
  }: IPostParams) => {
    return useMutation<T, AxiosError, T>(
      (requestData) => {
        return axiosApi.post(
          url,
          withFormData ? serialize(requestData, {}, undefined) : requestData
        );
      },
      {
        onSuccess: (data) => {
          withSuccessNotistack && handleApiSuccesswithNotistack();
          onSuccess && onSuccess(data);
        },
        onError: (error) => {
          withErrorNotistack && handleApiErrorwithNotistack(error);
          onError && onError(error);
        },
      }
    );
  };

  // Patch service
  const usePatchApi = <T,>({
    url,
    id,
    urlAfterSuccess,
    onSuccess,
    onError,
    withSuccessNotistack = true,
    withErrorNotistack = true,
  }: IPatchParams) => {
    return useMutation<T, AxiosError, T>(
      (requestData) => axiosApi.patch(`${url}/${id}`, requestData),
      {
        onSuccess: () => {
          withSuccessNotistack && handleApiSuccesswithNotistack();
          onSuccess && onSuccess();
        },
        onError: (error) => {
          withErrorNotistack && handleApiErrorwithNotistack(error);
          onError && onError(error);
        },
      }
    );
  };

  // For show snackbar when api return a error
  const handleApiErrorwithNotistack = (error: any) => {
    if (Array.isArray(error?.message)) {
      error?.message.forEach((error: any) => {
        enqueueSnackbar({
          variant: "error",
          message: error,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      });
    } else {
      enqueueSnackbar({
        variant: "error",
        message: error?.message,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    }
  };

  // For show snackbar when api successfully
  const handleApiSuccesswithNotistack = () => {
    enqueueSnackbar({
      variant: "success",
      message: "Completed successfully",
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  return {
    usePostApi,
    usePatchApi,
  };
};

export default useApiServices;
